import React from 'react'

import Card from '../Card/Card'
import { Icon, Loader } from 'semantic-ui-react'
import isEqual from 'lodash/isEqual'

import {
  isFuture,
  isAfter,
  isThisMonth,
  isSameMonth,
  isSameDay,
  startOfMonth,
  addMonths,
  isBefore,
  format,
  getDaysInMonth
} from 'date-fns'

export default class Calendar extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      month: [],
      displayMonth: props.date || new Date()
    }
  }
  componentDidMount() {
    this.buildMonth()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.date !== this.props.date) {
      this.buildMonth()
    }
    if (!isEqual(prevProps.monthData, this.props.monthData)) {
      this.buildMonth()
    }
  }

  buildMonth = () => {
    let { date, monthData, earliestDate, today } = this.props
    const { displayMonth } = this.state
    today = today ? format(today) : new Date()

    earliestDate = earliestDate || startOfMonth(today)
    earliestDate = new Date(earliestDate)
    monthData = monthData || []
    // reset month
    const month = []
    const mDate = startOfMonth(displayMonth)
    // use date-fns to figure out which day of the week this first day of this month is
    // push blank 'days' to the month to offset it correctly so 1 ends up on the right day
    // it is zero based, so 'Wednesday' === 3, which would require adding 3 days, perfect
    const blankDaysToAddToStart = mDate.getDay()
    if (blankDaysToAddToStart > 0) {
      for (let i = 0; i < blankDaysToAddToStart; i++) {
        month.push({ label: '', content: '', className: '' })
      }
    }
    // next use date-fns to figure out the last day of the month
    const daysInMonth = getDaysInMonth(mDate)
    // next loop thru 1 thru last day in month, pushing days to month, and finding corresponding
    // content to those days
    for (let d = 1; d <= daysInMonth; d++) {
      const iDate = `${mDate.getFullYear()}-${this.leftpad(
        mDate.getMonth() + 1,
        2
      )}-${this.leftpad(d, 2)}`
      const iDateDate = new Date(format(iDate))

      const dayOfMonth = monthData.find((day) => day.date === iDate)
      const content = dayOfMonth ? dayOfMonth.content : ''
      let className = dayOfMonth ? dayOfMonth.className : ''
      if (isSameDay(iDateDate, today)) {
        className += ' today'
      } else if (
        isAfter(iDateDate, today) ||
        isBefore(iDateDate, earliestDate)
      ) {
        className += ' disabled'
      }
      if (isSameDay(date, iDateDate)) {
        className += ' selected'
      }
      month.push({ label: d, content, className, date: iDate })
    }
    // next see if our total, offset + days in month is divisible by 7, if not, add enough to the
    // end to make it even.
    const currentLength = month.length
    const needed = Math.ceil(currentLength / 7) * 7 - currentLength

    for (let n = 0; n < needed; n++) {
      month.push({ label: '', content: '', className: '' })
    }

    this.setState({ month, isLoading: false })
  }

  leftpad = (str, length, char = '0') => {
    str = str + '' // make sure its a string
    return Array(length - str.length + 1).join(char) + str
  }

  selectMonth = (selectedMonth) => {
    this.setState({ displayMonth: selectedMonth }, () => this.buildMonth())
  }

  get isANextMonth() {
    const nextMonth = addMonths(this.state.displayMonth, 1)
    return !(isFuture(nextMonth) && !isThisMonth(nextMonth))
  }
  get isAPrevMonth() {
    const prevMonth = addMonths(this.state.displayMonth, -1)
    let { earliestDate } = this.props
    if (!earliestDate) {
      return true
    }
    earliestDate = new Date(earliestDate)
    return (
      isBefore(earliestDate, prevMonth) || isSameMonth(prevMonth, earliestDate)
    )
  }

  nextMonth = () => {
    const nextMonth = addMonths(this.state.displayMonth, 1)
    if (this.isANextMonth) {
      this.selectMonth(nextMonth)
      if (
        this.props.onMonthChange &&
        typeof this.props.onMonthChange === 'function'
      ) {
        this.props.onMonthChange(nextMonth)
      }
    }
  }

  prevMonth = () => {
    const prevMonth = addMonths(this.state.displayMonth, -1)
    if (this.isAPrevMonth) {
      this.selectMonth(prevMonth)
      if (
        this.props.onMonthChange &&
        typeof this.props.onMonthChange === 'function'
      ) {
        this.props.onMonthChange(prevMonth)
      }
    }
  }

  handleDayClick = (day) => {
    if (
      !day.date ||
      (day.className && day.className.indexOf('disabled') !== -1)
    ) {
      return false
    }
    this.props.onDateSelect(day.date)
  }

  render() {
    const { displayMonth, month } = this.state
    const { size, isLoading } = this.props

    return (
      <Card style={{ display: 'inline-block' }} className='calendar-container'>
        <div className='month-select'>
          <Icon
            name='chevron left'
            onClick={this.prevMonth}
            disabled={!this.isAPrevMonth}
          />
          <span>{format(displayMonth, 'MMMM YYYY')}</span>
          <Icon
            name='chevron right'
            onClick={this.nextMonth}
            disabled={!this.isANextMonth}
          />
        </div>
        <div className={`Calendar ${size || 'small'}`}>
          <div className='heading'>
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
              <Day key={i}>{d}</Day>
            ))}
          </div>
          {isLoading ? (
            <Loader active />
          ) : (
            month.map((day, index) => (
              <Day
                key={index}
                label={day.label || ''}
                className={day.className || ''}
                onClick={() => this.handleDayClick(day)}
              >
                {day.content}
              </Day>
            ))
          )}
        </div>
        <div className='calendar-legend'>
          <div className='group success'>
            <Icon name='circle outline' />
            <span>Success</span>
          </div>
          <div className='group failed'>
            <Icon name='square outline' />
            <span>Failed</span>
          </div>
        </div>
      </Card>
    )
  }
}

const Day = ({ label, children, className, ...otherProps }) => {
  return (
    <div className={`day ${className || ''}`} {...otherProps}>
      {label && <span className='day-label'>{label}</span>}
      {children}
    </div>
  )
}
